import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const LAST_UPDATED = 'April 29, 2026';
const COMPANY = 'HealthClear AI, LLC';
const EMAIL = 'legal@healthclearai.ai';
const ADDRESS = '6900 Tavistock Lakes Blvd. Suite 400, Orlando FL, 32827';

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10">
      <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-3 pb-2 border-b border-slate-100 dark:border-slate-700">{title}</h2>
      <div className="space-y-3 text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

export default function TermsPage() {
  const { dark } = useTheme();
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img src={dark ? '/logo-dark.svg?v=3' : '/logo.svg?v=3'} alt="HealthClear AI" className="h-9 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 dark:text-slate-400 hover:text-sky-600 transition-colors">← Back to Home</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title block */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Terms of Service</h1>
          <p className="text-sm text-slate-400 dark:text-slate-500">Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Effective: {LAST_UPDATED}</p>
        </div>

        {/* Important disclaimer */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 mb-10">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-200 text-sm">Not Medical Advice</p>
              <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">HealthClear AI provides informational analysis of your health records for educational purposes only. The content is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions you may have regarding a medical condition.</p>
            </div>
          </div>
        </div>

        {/* TOC */}
        <nav className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-10">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Contents</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-sky-600">
            {[
              ['acceptance', 'Acceptance of Terms'],
              ['eligibility', 'Eligibility'],
              ['description', 'Description of Service'],
              ['not-medical', 'Not Medical Advice'],
              ['accounts', 'Accounts and Security'],
              ['subscriptions', 'Subscriptions and Billing'],
              ['health-data', 'Health Plan Data Authorization'],
              ['acceptable-use', 'Acceptable Use'],
              ['ip', 'Intellectual Property'],
              ['privacy', 'Privacy'],
              ['disclaimer', 'Disclaimer of Warranties'],
              ['liability', 'Limitation of Liability'],
              ['indemnification', 'Indemnification'],
              ['termination', 'Termination'],
              ['governing-law', 'Governing Law'],
              ['changes', 'Changes to Terms'],
              ['contact', 'Contact'],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-sky-800 transition-colors">{label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <Section id="acceptance" title="1. Acceptance of Terms">
          <p>
            By accessing or using the HealthClear AI platform (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms form a legally binding contract between you and {COMPANY}.
          </p>
        </Section>

        <Section id="eligibility" title="2. Eligibility">
          <p>You must be at least 18 years old and capable of forming a binding contract to use the Service. By using the Service, you represent that you meet these requirements. The Service is intended for use by individuals managing their own healthcare information or by authorized caregivers acting on behalf of another person with their consent.</p>
        </Section>

        <Section id="description" title="3. Description of Service">
          <p>HealthClear AI provides AI-powered analysis of medical documents and health plan records to help users understand their health information in plain language. Features include:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Upload and analysis of medical reports, lab results, and clinical documents</li>
            <li>Health plan connection to retrieve claims, benefits, and clinical records</li>
            <li>Trend analysis and health summary dashboards</li>
            <li>Appointment preparation assistance</li>
            <li>Medication interaction checks (informational only)</li>
            <li>Secure report sharing with healthcare providers</li>
          </ul>
          <p>We reserve the right to modify, suspend, or discontinue features with reasonable notice.</p>
        </Section>

        <Section id="not-medical" title="4. Not Medical Advice">
          <p>
            <strong className="text-slate-800">THE SERVICE IS NOT A MEDICAL DEVICE AND DOES NOT PROVIDE MEDICAL ADVICE.</strong> All analyses, summaries, care gap suggestions, and recommendations generated by the Service are for informational and educational purposes only. They do not constitute medical advice, diagnoses, treatment recommendations, or prescriptions.
          </p>
          <p>Do not disregard or delay seeking professional medical advice because of information you received from HealthClear AI. In an emergency, call 911 or your local emergency services immediately.</p>
        </Section>

        <Section id="accounts" title="5. Accounts and Security">
          <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately at <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a> if you suspect unauthorized access to your account. We are not liable for losses resulting from unauthorized use of your credentials.</p>
          <p>You may not create accounts on behalf of others without their explicit consent, share your account, or attempt to access other users' accounts.</p>
        </Section>

        <Section id="subscriptions" title="6. Subscriptions and Billing">
          <p>HealthClear AI offers free and paid subscription tiers. Paid subscriptions are billed on a monthly or annual basis via Stripe.</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Free trial:</strong> Where offered, a 30-day free trial begins on the date of subscription. No charge is made until the trial period ends.</li>
            <li><strong>Cancellation:</strong> You may cancel at any time from your Profile. Access continues through the end of the current billing period; no partial refunds are issued.</li>
            <li><strong>Refunds:</strong> We offer refunds within 7 days of initial charge for annual subscriptions if you have not used premium features. Contact <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a>.</li>
            <li><strong>Price changes:</strong> We will notify you at least 30 days before any price change affecting your active subscription.</li>
          </ul>
        </Section>

        <Section id="health-data" title="7. Health Plan Data Authorization">
          <p>
            When you connect your health plan through our Flexpa integration, you are authorizing both HealthClear AI and Flexpa to retrieve your health plan records via the FHIR Patient Access API. This authorization is voluntary. You can revoke it at any time from Profile → Health Connections.
          </p>
          <p>By connecting your health plan, you represent that:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>You are the subscriber on the plan, a covered dependent, or have lawful authority to access the plan data.</li>
            <li>You have reviewed and agree to <a href="https://flexpa.com/terms" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">Flexpa's Terms of Service</a>.</li>
            <li>You consent to the data uses described in our <Link to="/privacy" className="text-sky-600 underline">Privacy Policy</Link>.</li>
          </ul>
        </Section>

        <Section id="acceptable-use" title="8. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Upload documents that are not your own health records or records you are authorized to process.</li>
            <li>Attempt to reverse-engineer, scrape, or extract data from the Service in bulk.</li>
            <li>Use the Service for any unlawful purpose or in violation of any third party's rights.</li>
            <li>Interfere with the security, integrity, or availability of the Service.</li>
            <li>Share your account credentials or allow others to access the Service through your account.</li>
            <li>Use automated means to access the Service without our written permission.</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>
        </Section>

        <Section id="ip" title="9. Intellectual Property">
          <p>All content, features, and technology comprising the Service — including software, UI design, logos, and AI models — are owned by {COMPANY} or its licensors and protected by applicable IP laws.</p>
          <p><strong>Your data:</strong> You retain all rights to your uploaded health documents and health plan data. By using the Service, you grant us a limited license to process your data solely for providing the Service to you.</p>
          <p><strong>Analysis outputs:</strong> Plain-language analyses and reports generated for you are owned by you.</p>
        </Section>

        <Section id="privacy" title="10. Privacy">
          <p>Your use of the Service is governed by our <Link to="/privacy" className="text-sky-600 underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. By using the Service, you consent to the data practices described therein.</p>
        </Section>

        <Section id="disclaimer" title="11. Disclaimer of Warranties">
          <p className="uppercase text-xs font-semibold text-slate-500">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI-GENERATED ANALYSES WILL BE ACCURATE OR COMPLETE.
          </p>
        </Section>

        <Section id="liability" title="12. Limitation of Liability">
          <p className="uppercase text-xs font-semibold text-slate-500">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, HEALTHCLEAR AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF HEALTH OUTCOMES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
        </Section>

        <Section id="indemnification" title="13. Indemnification">
          <p>You agree to indemnify and hold harmless {COMPANY} and its officers, directors, employees, and agents from any claims, losses, or damages (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any third party's rights.</p>
        </Section>

        <Section id="termination" title="14. Termination">
          <p>You may terminate your account at any time from Profile → Account → Delete Account. We may suspend or terminate your access immediately if you violate these Terms or if we are required to do so by law. Upon termination, your data will be deleted per our <Link to="/privacy" className="text-sky-600 underline">Privacy Policy</Link> retention schedule.</p>
        </Section>

        <Section id="governing-law" title="15. Governing Law">
          <p>These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles. Any disputes shall be resolved in the state or federal courts located in Orange County, Florida. You waive any objection to jurisdiction or venue in such courts.</p>
        </Section>

        <Section id="changes" title="16. Changes to Terms">
          <p>We may update these Terms from time to time. We will notify you of material changes by email and in-app notice at least 30 days before the new terms take effect. Continued use of the Service after the effective date constitutes acceptance.</p>
        </Section>

        <Section id="contact" title="17. Contact">
          <p>Questions about these Terms:</p>
          <ul className="list-none space-y-1 ml-2">
            <li><strong>Email:</strong> <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a></li>
            <li><strong>Mail:</strong> {COMPANY}, {ADDRESS}</li>
          </ul>
        </Section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 dark:border-slate-800 py-8 text-center">
        <p className="text-xs text-slate-400">
          © 2026 HealthClear AI · <Link to="/privacy" className="hover:text-slate-600">Privacy Policy</Link> · <Link to="/terms" className="hover:text-slate-600">Terms of Service</Link>
        </p>
        <p className="text-xs text-slate-400 mt-2">
          {ADDRESS} · <a href="tel:6893292013" className="hover:text-slate-600">689-329-2013</a>
        </p>
      </footer>
    </div>
  );
}
